import type { ProductHit } from '@libs/algolia';
import { AlgoliaIndice, getProductUrl } from '@libs/algolia';

import type { ProductSearchItem, QuerySearchItem } from '../search.types';

export type RawQuerySuggestion = {
    objectID?: string;
    query: string;
    _highlightResult?: { query?: { matchedWords?: string[]; matchLevel?: string } };
    [indexName: string]: unknown;
};

export type RawProductSuggestion = ProductHit;

export type RawSearchResponse<Item> = {
    hits: Item[];
    query: string;
    queryID?: string;
    renderingContent?: { redirect?: { url?: string } };
};

export type SearchRedirectResponse = {
    sourceId: 'querySuggestionsPlugin' | 'productSuggestionsPlugin';
    query: string;
    url?: string;
};

function readCategory(hit: RawQuerySuggestion) {
    const products = hit[AlgoliaIndice.PRODUCTS] as
        | {
              facets?: {
                  exact_matches?: {
                      primary_category_name?: Array<{ value: string }>;
                  };
              };
          }
        | undefined;
    const categories = products?.facets?.exact_matches?.primary_category_name?.map(category => category.value) ?? [];

    return {
        found: categories.length > 0,
        value: categories[0],
    };
}

export function mapQueryItems(hits: RawQuerySuggestion[], query: string) {
    let hasCategory = false;
    const queryCollectionLabels: string[] = [];

    const items: QuerySearchItem[] = hits.map(hit => {
        const category = !query || hasCategory ? undefined : readCategory(hit);
        hasCategory ||= category?.found ?? false;
        queryCollectionLabels.push(hit.query);
        if (category?.found) queryCollectionLabels.push(hit.query);

        return {
            kind: 'query',
            id: hit.objectID ?? hit.query,
            label: hit.query,
            category: category?.value,
            highlight: {
                matchedWords: hit._highlightResult?.query?.matchedWords ?? [],
                matchLevel: hit._highlightResult?.query?.matchLevel ?? 'none',
            },
        };
    });

    return { items, queryCollectionLabels };
}

export function mapProductItems(hits: RawProductSuggestion[], queryId?: string): ProductSearchItem[] {
    return hits.map(hit => ({
        kind: 'product',
        hasProductId: Boolean(hit.id),
        id: hit.objectID ?? String(hit.id),
        label: hit.name ?? hit.objectID ?? '',
        skuId: String(hit.id ?? ''),
        masterProductId: hit.masterProductID,
        objectId: hit.objectID,
        queryId,
        image: { large: hit.images?.large, small: hit.images?.small },
        price: hit.priceData,
        masterProductUrl: getProductUrl(hit, false),
        skuProductUrl: getProductUrl(hit, true),
    }));
}

export function getRedirectResponse(
    sourceId: SearchRedirectResponse['sourceId'],
    response: RawSearchResponse<unknown>
): SearchRedirectResponse {
    return {
        sourceId,
        query: response.query,
        url: response.renderingContent?.redirect?.url,
    };
}
